import { useEffect, useState } from "react";
import { api, type Session, type Today } from "../api.ts";
import { colors, longDate, serif, tokens } from "../theme.ts";
import { GlassButton, Segment } from "../components/glass.tsx";
import { Morning } from "./Morning.tsx";
import { Evening } from "./Evening.tsx";
import { Appointments, WorkingOnStrip } from "../components/WorkingOn.tsx";

type Kind = "morning" | "evening";

const OPTIONS: { key: Kind; label: string }[] = [{ key: "morning", label: "Morning" }, { key: "evening", label: "Evening" }];

/** Morning before 12:00, evening from 12:00. Europe/Berlin, like every other time in the app. The Sunday weekly lives on the Week tab. */
function dueKind(): Kind {
  const hour = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin", hour: "numeric", hourCycle: "h23",
  }).formatToParts(new Date()).find((p) => p.type === "hour")?.value ?? "0");
  return hour < 12 ? "morning" : "evening";
}

/**
 * Home: today's appointments, the session that is due with a small segment
 * control to pick the other (decided 21.09: both), then the working-on strip
 * (V3). `manual` is not persisted, so the screen goes back to automatic on
 * the next visit instead of sticking to a choice made once.
 */
export function Home({
  data, onOpen, onOpenContainer, onChanged, onLock,
}: {
  data: Today; onOpen: (id: string) => void; onOpenContainer: (key: string) => void;
  onChanged: () => void; onLock: () => void;
}) {
  const [manual, setManual] = useState<Kind | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);

  const refetchSessions = () => { api.sessions(7).then(setSessions).catch(() => {}); };
  useEffect(refetchSessions, [data.date]);

  const kind = manual ?? dueKind();
  const doneToday = kind === "evening" ? sessions.find((s) => s.kind === "evening" && s.date === data.date) ?? null : null;

  return (
    <div style={{ padding: `12px ${tokens.space.gutter}px 0` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div style={{
          fontFamily: serif, fontSize: tokens.type.screenTitle.size, lineHeight: tokens.type.screenTitle.lineHeight, color: colors.ink,
        }}>Home</div>
        <div style={{ fontSize: 13, color: colors.ink }}>{longDate(data.date)}</div>
      </div>

      <Appointments items={data.appointments ?? []} onOpen={onOpen} />

      <Segment options={OPTIONS} value={kind} onChange={setManual} />

      <div style={{ marginTop: 16 }}>
        {kind === "morning"
          ? <Morning data={data} onOpen={onOpen} onFlagsChanged={onChanged} />
          : <Evening data={data} doneToday={doneToday} onOpen={onOpen} onWritten={() => { refetchSessions(); onChanged(); }} />}
      </div>

      <WorkingOnStrip groups={data.working_on ?? []} onOpen={onOpenContainer} />

      <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
        <GlassButton variant="default" onClick={onLock}>Lock</GlassButton>
      </div>
    </div>
  );
}
